import { compose, get, isEmpty } from 'lodash/fp';
import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { composeProps, createAsyncMetaSelectors, createPropsExtender, createScopedSelector } from '@mews/framework';
import { ResourceCategoryType } from '@mews-commander/legacy-data-structures';
import { getStringProp } from '@mews/fp';
import { ResourceCategory } from '@mews-commander/data-access';
import { notNil, getOrNull } from '@mews-commander/utils';
import { areVouchersEnabled } from '@mews-commander/legacy-entities/src/ducks/subscriptions';
import { getServiceEnterpriseSubscriptionId } from '@mews-commander/legacy-entities/src/selectors/services';
import { getSortedResourceCategories } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getAvailabilityAdjustmentsUniqResourceCategoryIds } from '@mews-commander/legacy-entities/src/ducks/availabilityAdjustments';
import { ApplicationState } from '@mews-commander/types';
import { INITIAL_STATE } from '../reducers/screens';
import { moduleSelector } from '../module';

const getScreenScopes = createSelector(moduleSelector, get('screens'));
export const getScreen = createScopedSelector({ parentSelector: getScreenScopes, initialState: INITIAL_STATE });

// selected service id
export const getServiceId = createSelector(getScreen, (screen) => screen.serviceId);

export const isServiceIdNotSelected = compose(isEmpty, getServiceId);

export const areServiceVouchersEnabled = composeProps(
    areVouchersEnabled,
    createPropsExtender('subscriptionId', getServiceEnterpriseSubscriptionId)
);

export const getSelectedReservationPlaceId = compose(get('reservationPlaceId'), getScreen);
export const hasSelectedReservationPlace = compose(notNil, getSelectedReservationPlaceId);
export const getNextReservationPlaceId = compose(get('nextReservationPlaceId'), getScreen);
export const isChangingReservationPlace = createSelector(getScreen, (s) => !!s.nextReservationPlaceId);

export const getShowChangeDateDialog = createSelector(getScreen, (s) => s.showChangeDateDialog);
export const getShowChangeServiceDialog = createSelector(getScreen, (s) => s.showChangeServiceDialog);

export const getCurrentStep = createSelector(getScreen, (screen) => screen.currentStep);
export const getPreviousStep = createSelector(getScreen, (screen) => screen.previousStep);

const getMeta = compose(get('meta'), getScreen);
export const [isFetching, hasFailed] = createAsyncMetaSelectors(getMeta);
export const isCreatingGroup = createSelector(getScreen, (s) => !!s.isCreatingGroup);

// @ts-expect-error
export const isNRSLoaderShown = (state: ApplicationState) => state.newReservation.loader.showLoader;

export const getNextServiceId = createSelector(getScreen, (s) => s.nextServiceId);
export const isChangingService = createSelector(getScreen, (s) => !!s.nextServiceId);

export const getReservationGroupUrl = createSelector(getScreen, (s) => s.reservationGroupUrl);
export const showConfirmation = createSelector(getReservationGroupUrl, (u) => !!u);

export const getSelectedResourceCategoryType = createSelector(getScreen, (screen) =>
    ResourceCategoryType.getByName(screen.resourceCategoryType)
);

export const getSelectedResourceCategoryTypeName = compose(get('name'), getSelectedResourceCategoryType);

export const isSelectedEnterpriseInMultiTaxEnvironment = createSelector(
    getScreen,
    (screen) => screen.isMultiTaxEnvironment || false
);

export const getSelectedAvailabilityBlockId = compose(getOrNull('availabilityBlockId'), getScreen);

const getAvailablityBlockResourceCategoriesByType = createSelector(
    getStringProp('resourceCategoryTypeName'),
    getSortedResourceCategories,
    getAvailabilityAdjustmentsUniqResourceCategoryIds,
    (resourceCategoryTypeName, serviceResourceCategories, adjustmentsResourceCategoryIds) =>
        pipe(
            // @ts-expect-error
            serviceResourceCategories,
            array.filterMap((resourceCategory: ResourceCategory) => {
                const { id, type } = resourceCategory;
                return type === resourceCategoryTypeName && adjustmentsResourceCategoryIds.includes(id)
                    ? option.some(resourceCategory)
                    : option.none;
            })
        )
);

export const getAvailablityBlockResourceCategoryIdsByType = flow(
    getAvailablityBlockResourceCategoriesByType,
    array.map(({ id }) => id)
);
